import { QueryKey, useQueries } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";

import { useAuth } from "@/providers";
import { useDomainServices } from "@/providers/DomainServices";

import { MetadataValuesRequest, QueryTarget } from "../QueryRecord";

/**
 * Convert a metadata column name to a metadata key identifier.
 *
 * @param metadataColumn a selectable metadata column name with the "metadata." prefix
 * @returns the metadata column name with the prefix stripped
 */
function metadataColumnToKey(metadataColumn: string): string {
  const prefix = "metadata.";

  if (metadataColumn.startsWith(prefix)) {
    return metadataColumn.slice(prefix.length);
  }

  return metadataColumn;
}

/**
 * Query cache keys for metadata value queries.
 */
export class MetadataValueQueryKeys {
  /**
   * Query key prefix for all cached queries for a given metadata key.
   *
   * Used for query invalidation when a user modifies entity metadata.
   *
   * @param target query target
   * @param metadataKey metadata key
   * @returns a query key prefix
   */
  static forKey(target: QueryTarget, metadataKey: string): QueryKey {
    return ["metadataValues", target, metadataKey];
  }

  /**
   * Query key for cached metadata values for a set of entities.
   *
   * @param target query target
   * @param metadataKey requested metadata key
   * @param entityIds Roboto entity IDs
   * @returns a query key array
   */
  static forKeyAndEntities(
    target: QueryTarget,
    metadataKey: string,
    entityIds: readonly string[],
  ): QueryKey {
    return ["metadataValues", target, metadataKey, [...entityIds].sort()];
  }
}

/**
 * Parameters passed to the `useMetadataValues` hook.
 */
interface UseMetadataValuesParams {
  target: QueryTarget;
  metadataColumns: string[];
  entityIds: string[];
  enabled?: boolean; // true by default
}

export type UseMetadataValuesResult = {
  getMetadataValue: (metadataKey: string, entityId: string) => unknown;
};

export function useMetadataValues({
  target,
  metadataColumns,
  entityIds,
  enabled,
}: UseMetadataValuesParams): UseMetadataValuesResult {
  const { queryService } = useDomainServices();
  const { currentOrganization } = useAuth();

  const metadataKeys = useMemo(
    () => metadataColumns.map(metadataColumnToKey),
    [metadataColumns],
  );

  const queries = useQueries({
    queries: metadataKeys.map((key) => ({
      queryKey: MetadataValueQueryKeys.forKeyAndEntities(
        target,
        key,
        entityIds,
      ),
      queryFn: ({ signal }: { signal?: AbortSignal }) => {
        if (currentOrganization === null) {
          return Promise.reject(
            new Error("[useMetadataValues] Unable to retrieve current org!"),
          );
        }

        const request: MetadataValuesRequest = {
          target: target,
          metadata_key: key,
          entity_ids: entityIds,
        };

        return queryService.queryMetadataValues(request, {
          resourceOwnerId: currentOrganization.org_id,
          abortSignal: signal,
        });
      },
      enabled:
        (enabled ?? true) && entityIds.length > 0 && metadataKeys.length > 0,
      // Metadata values for a given key don't change too frequently, making them cacheable.
      staleTime: 2 * 60 * 1000,
    })),
  });

  const getMetadataValue = useCallback(
    (metadataColumn: string, entityId: string): unknown => {
      const metadataKey = metadataColumnToKey(metadataColumn);
      const idx = metadataKeys.indexOf(metadataKey);
      if (idx === -1) {
        return undefined;
      }

      const metadataQuery = queries[idx];
      if (metadataQuery.isError) {
        return "Unable to fetch metadata.";
      }

      return metadataQuery.data?.metadata_values?.[entityId];
    },
    [metadataKeys, queries],
  );

  return {
    getMetadataValue,
  };
}
